import { useEffect, useState, FocusEvent } from 'react';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import { Rifm } from 'rifm';

import { validateCpr, ValidateCpr } from '../../shared/validators/validate-cpr';
import { formatCprNumber } from '../../shared/formatters/format-cpr';
import { generateUniqueId } from '../../shared/helpers/helpers';

// to remove dash from CPR
function trimCpr(cpr: string): string {
    const dash = /-/g;
    return cpr.replace(dash, '');
}

type OnChanges = {
    valid: boolean;
    value: string;
    displayValue: string;
    age: number | undefined;
};

type CprFieldProps = {
    onChanges(val: OnChanges): void;
    label?: string;
    value?: string;
    isSubmitted?: boolean;
    minAge?: number;
    maxAge?: number;
    required?: boolean;
    disabled?: boolean;
    errorTextInvalid?: string;
    errorTextYoung?: string;
    errorTextOld?: string;
} & TextFieldProps;

type IState = {
    fieldValue: string;
    age: number | undefined;
    isError: boolean;
    errorText: string | undefined;
};

const CprField = ({
    onChanges,
    label = 'CPR-nummer',
    value = '',
    isSubmitted = false,
    minAge = 18,
    maxAge = 140,
    required = false,
    disabled = false,
    errorTextInvalid = 'Det indtastede nummer er ikke et gyldigt CPR-nummer.',
    errorTextYoung = `Minimumsalder ${minAge} år`,
    errorTextOld = `Maximum alder ${maxAge} år`,
    ...rest
}: CprFieldProps) => {
    const [state, setState] = useState<IState>({
        fieldValue: value, // Value of the field
        age: undefined,
        isError: false, // Error state
        errorText: undefined,
    });

    useEffect(() => {
        handleChange(formatCprNumber(value));
    }, [value]);

    function getAgeValidationErrorText(validation: ValidateCpr): string | undefined {
        if (!validation.valid) {
            return errorTextInvalid;
        }
        if (validation.age && validation.age < minAge) {
            return errorTextYoung;
        }
        if (validation.age && validation.age > maxAge) {
            return errorTextOld;
        }
        return undefined;
    }

    function handleChange(formattedCpr: string) {
        // reset validation error first
        setState((previousState) => ({ ...previousState, isError: false, errorText: undefined }));

        const changedValue = formattedCpr;
        const trimmed = trimCpr(changedValue);

        // only validate when user is done typing
        if (trimmed.length === 10) {
            const validation = validateCpr(trimmed);
            const errorText = getAgeValidationErrorText(validation);
            setState((pState) => ({
                ...pState,
                fieldValue: trimmed,
                age: validation.age,
                isError: !validation.valid || !!errorText, // if not valid or age out of bounds
                errorText: errorText,
            }));
            onChanges({
                valid: !(!validation.valid || !!errorText), // if not valid or age out of bounds
                value: trimmed,
                displayValue: changedValue,
                age: validation.age,
            });
        } else {
            setState((pState) => ({ ...pState, fieldValue: trimmed, age: undefined }));
            onChanges({
                valid: false, // if not valid or age out of bounds
                value: trimmed,
                displayValue: changedValue,
                age: undefined,
            });
        }
    }

    function handleBlur(event: FocusEvent<HTMLInputElement>) {
        // reset validation error first
        setState((previousState) => ({ ...previousState, isError: false, errorText: undefined }));

        const { value } = event.target;
        const trimmedValue = trimCpr(value);
        const validation = validateCpr(trimmedValue);
        const errorText = getAgeValidationErrorText(validation);
        setState((previousState) => ({
            ...previousState,
            fieldValue: value,
            age: validation.age,
            isError: !validation.valid || !!errorText, // if not valid or age out of bounds
            errorText: errorText,
        }));
        handleChange(formatCprNumber(value));
    }

    function handleFocus() {
        setState((previousState) => ({ ...previousState, isError: false, errorText: undefined }));
    }

    return (
        <Rifm
            accept={/\d/g}
            mask={state.fieldValue.length >= 11}
            format={formatCprNumber}
            value={state.fieldValue}
            onChange={handleChange}
        >
            {({ value, onChange }) => (
                <TextField
                    error={state.isError && isSubmitted}
                    required={required}
                    disabled={disabled}
                    id={`CprField-${generateUniqueId()}`}
                    label={label}
                    type="tel" /* show numeric keypad on mobile */
                    helperText={state.isError && isSubmitted && state.errorText}
                    onChange={onChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    value={value}
                    autoComplete="off"
                    fullWidth
                    margin="normal"
                    {...rest}
                />
            )}
        </Rifm>
    );
};

export { CprField };
export type { CprFieldProps };
export type { OnChanges };
